/**
 * Nest Runtime Capabilities API
 *
 * Provides detection of Nest availability and version.
 *
 * Usage:
 *   - Call `initCodeModuleCapabilities()` once during app startup
 *   - Use `isCodeModuleAvailable()` synchronously anywhere after init
 */

import { isNestApiAvailable, requestNestMethod } from "../nest/client"

export interface CodeModuleCapabilities {
    enabled: boolean
    version: string
}

let cachedCapabilities: CodeModuleCapabilities | null = null

export function resetCodeModuleCapabilities(): void {
    cachedCapabilities = null
}

/**
 * Initialize code module capabilities by querying Nest.
 * Call this once during app startup (e.g., in CodeLayout init).
 * After this call, `isCodeModuleAvailable()` returns the real value.
 */
export async function initCodeModuleCapabilities(): Promise<CodeModuleCapabilities> {
    if (cachedCapabilities) return cachedCapabilities

    if (!isNestApiAvailable()) {
        throw new Error("Nest API is required in the Electron renderer.")
    }

    // system.initialize is allowed for every authenticated principal, including
    // a colleague's scoped share; system.status.read is node-only.
    const response = await requestNestMethod("system.initialize", { clientName: "WildLark", protocolVersion: 1 })
    cachedCapabilities = { enabled: true, version: response.serverVersion ?? "nest" }
    return cachedCapabilities
}

/**
 * Check if the code module is available.
 * Returns `false` before `initCodeModuleCapabilities()` is called,
 * or if Nest is unavailable.
 */
export function isCodeModuleAvailable(): boolean {
    if (!cachedCapabilities) return isNestApiAvailable()
    return cachedCapabilities.enabled
}

export interface SdkCapabilities {
    slash_commands: string[]
    skills: string[]
    plugins: { name: string; path: string }[]
    cachedAt: number
}

/**
 * Fetch SDK capabilities for a working directory.
 * Returns cached data if available, otherwise runs a lightweight probe.
 */
export async function getSdkCapabilities(cwd: string): Promise<SdkCapabilities | null> {
    void cwd
    return null
}
